/* eslint-disable no-shadow */
import { useContext, useEffect, useRef, useState } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";
import _ from "lodash";
import router from "next/router";
import toast from "react-hot-toast";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";

import SafeConnectRequiredFieldsBanner from "../SafeConnectRequiredFieldsBanner";
import DDQManagement from "./DDQManagement";

import { Button } from "~/components/Button";
import * as DetailList from "~/components/DetailList";
import { Box, Padding, Separator } from "~/components/Elements";
import { BackButtonContext } from "~/contexts/BackButtonContext";
import {
  performDocumentAccessQuery,
  useDDQDocument,
  useDocumentRequests,
  useTfProfileMetrics,
} from "~/endpoints/trustFramework";
import {
  DocumentAccessAction,
  DocumentRequest,
  DocumentRequestInput,
  DocumentRequestStatus,
  DocumentType,
} from "~/endpoints/trustFramework/types";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID";
import { BlurredDiv } from "~/templates/companies/styles";
import SVGApprove from "~/templates/transactions/components/TransactionRow/components/SVGApprove";
import { VASP } from "~/types/vaspid";

const TooltipContentContainer = styled.div`
  max-width: 215px;
  font-size: 12px;
  padding: 4px 8px;
`;

interface VASPVerificationsProps {
  vasp: VASP;
  userIsAuthenticated?: boolean;
  onClickViewDDQ?: () => void;
  hasCompletedForm?: boolean;
  allRequiredFieldsComplete?: boolean;
}
export default function VASPVerifications({
  vasp,
  userIsAuthenticated = false,
  onClickViewDDQ,
  hasCompletedForm,
  allRequiredFieldsComplete = false,
}: VASPVerificationsProps) {
  const ddqRef = useRef(null);
  const userVASPDID = useCurrentViewerVaspDID();
  const isVASPOwner = !!userVASPDID && userVASPDID === vasp?.did;
  const { data: ddq, isLoading } = useDDQDocument(vasp?.did, isVASPOwner ? undefined : userVASPDID);
  const { ddqFlowRequiredFields: isEnabledDdqFlowRequiredFieldsFF } = useFlags();

  useEffect(() => {
    if (window.location.href.includes("#ddq")) {
      (ddqRef?.current as any)?.scrollIntoView();
    }
  }, []);

  // Don't show the section if there is no document to show
  if (!vasp) {
    return <></>;
  }

  const updatedDate = ddq?.updatedDate as string;

  function handleClickView() {
    router.push(`/forms/ddq/view?ownerVASPdid=${vasp.did}`);
  }

  return (
    <Box style={{ width: "100%" }}>
      <Padding sized="medium">
        <BlurredDiv userIsAuthenticated={userIsAuthenticated}>
          <DetailList.Header>
            <DetailList.Item mobile={12} tablet={12} desktop={9}>
              <DetailList.Title as="h2">Counterparty Due Diligence</DetailList.Title>
            </DetailList.Item>
          </DetailList.Header>

          <DetailList.Content ref={ddqRef} id="ddq">
            <DetailList.Item mobile={12} desktop={5}>
              <DetailList.Term>Type</DetailList.Term>
              <DetailList.Description className="localize-ignore" style={{ display: "flex", alignItems: "center" }}>
                {hasCompletedForm && <SVGApprove style={{ fontSize: "1.1rem", marginRight: 10 }} />}{" "}
                {DocumentType.questionnaire}
              </DetailList.Description>
            </DetailList.Item>
            <DetailList.Item mobile={6} desktop={2}>
              <DetailList.Term>Date</DetailList.Term>
              <DetailList.Description className="localize-ignore">
                {updatedDate
                  ? new Date(updatedDate).toLocaleDateString("en-CA")
                  : isEnabledDdqFlowRequiredFieldsFF
                  ? "Not filled yet"
                  : ""}
              </DetailList.Description>
            </DetailList.Item>
            <DetailList.Item
              style={{
                display: "flex",
                justifyContent: "flex-end",
                flexDirection: "row",
                flex: "1 1 0px",
                alignItems: "center !important",
              }}
              mobile={5}
              desktop={5}
            >
              {!isVASPOwner && (
                <NonVaspOwnerSection
                  vasp={vasp}
                  onClickViewDDQ={onClickViewDDQ}
                  userVASPDID={userVASPDID}
                ></NonVaspOwnerSection>
              )}
              {isVASPOwner && !isLoading && !_.isEmpty(ddq) && (
                <>
                  {isEnabledDdqFlowRequiredFieldsFF ? (
                    <>
                      {allRequiredFieldsComplete ? null : <SafeConnectRequiredFieldsBanner />}
                      <Button
                        as="a"
                        kind="primary"
                        disabled={!allRequiredFieldsComplete}
                        onClick={allRequiredFieldsComplete ? handleClickView : undefined}
                      >
                        View DDQ
                      </Button>
                    </>
                  ) : (
                    <Button as="a" kind="primary" onClick={handleClickView}>
                      View DDQ
                    </Button>
                  )}
                </>
              )}
            </DetailList.Item>
          </DetailList.Content>
        </BlurredDiv>

        {isVASPOwner && (
          <>
            <Separator />
            <DDQManagement
              vasp={vasp}
              hasCompletedForm={hasCompletedForm}
              userIsAuthenticated={userIsAuthenticated}
              allRequiredFieldsComplete={allRequiredFieldsComplete}
            ></DDQManagement>
          </>
        )}
      </Padding>
    </Box>
  );
}

function NonVaspOwnerSection({
  userVASPDID,
  vasp,
  onClickViewDDQ,
}: {
  userVASPDID: string;
  vasp: VASP;
  onClickViewDDQ?: () => void;
}) {
  const [requestingDocument, setRequestingDocument] = useState(false);
  const { setDDQBack } = useContext(BackButtonContext);
  const { data: _documentAccessRequests, mutate: reloadDocumentRequests, isLoading } = useDocumentRequests(
    vasp?.did,
    userVASPDID,
  );
  const { data: profileMetrics, isLoading: isProfileMetricsLoading } = useTfProfileMetrics(vasp?.did);
  const { ddqFlowRequiredFields: isEnabledDdqFlowRequiredFieldsFF } = useFlags();

  const documentAccessRequests = _documentAccessRequests?.filter(
    (doc: DocumentRequest) => doc.documentType === DocumentType.questionnaire,
  );

  const handleDocumentRequest = async (): Promise<boolean> => {
    try {
      const documentType = DocumentType.questionnaire;
      setRequestingDocument(true);
      await performDocumentAccessQuery(vasp?.did, {
        requestingVASPdid: userVASPDID,
        accessAction: DocumentAccessAction.REQUEST,
        documentType,
      } as DocumentRequestInput);
      reloadDocumentRequests();
      return true;
    } catch (error) {
      toast.error((error as Error).toString());
      return false;
    } finally {
      setRequestingDocument(false);
    }
  };

  const handleClickView = () => {
    setDDQBack(window.location.href);

    if (onClickViewDDQ) {
      onClickViewDDQ();
    } else {
      router.push(`/forms/ddq/view?ownerVASPdid=${vasp.did}`);
    }
  };

  const status = documentAccessRequests?.[0]?.status;
  const isPending =
    status === DocumentRequestStatus.PENDING || status === DocumentRequestStatus.WAITING_FOR_INFORMATION;

  return (
    <>
      {!isLoading && status === DocumentRequestStatus.APPROVED && (
        <Button as="a" kind="primary" onClick={handleClickView}>
          View DDQ
        </Button>
      )}
      {!isLoading && isPending && (
        <Button
          style={{
            border: "none",
            color: "#828282",
            pointerEvents: "none",
            textTransform: "capitalize",
          }}
          kind="light"
        >
          Access requested
        </Button>
      )}
      {!isLoading && !isPending && (!status || status !== DocumentRequestStatus.APPROVED) && (
        <>
          <Button
            data-for="request-access-tooltip"
            data-tip
            style={{
              border: "none",
              color: "#72e85f",
            }}
            kind="light"
            onClick={() => {
              handleDocumentRequest();
              toast.success("Request sent");
            }}
            loading={requestingDocument}
            disabled={isEnabledDdqFlowRequiredFieldsFF && !isProfileMetricsLoading && !profileMetrics?.is_reachable}
          >
            Request Access
          </Button>
          {isEnabledDdqFlowRequiredFieldsFF && !isProfileMetricsLoading && !profileMetrics?.is_reachable && (
            <ReactTooltip id="request-access-tooltip">
              <TooltipContentContainer>
                Currently we’re not able to reach this company to send your request. Please check again later.
              </TooltipContentContainer>
            </ReactTooltip>
          )}
        </>
      )}
    </>
  );
}
